import NavigationItem from '@modules/utilities/interfaces/NavigationItem';
import NavigationSectionItem from '@modules/utilities/interfaces/NavigationSectionItem';
import { TNavigationContentItem } from '@rbx/docsite-utilities';
import TreeNode from '../interfaces/TreeNode';

export const treeDelimiter = '/';
type CustomRenderTreeNode = (item: TreeNodeNav) => React.ReactNode;

const startSlashRegex = /^\//;
const endSlashRegex = /\/$/;
const generateRelativePath = (basePath: string, relativePath: string) => {
  if (relativePath.startsWith('#')) return relativePath;
  if (relativePath.startsWith('https')) return relativePath;
  return `${basePath.replace(endSlashRegex, '')}/${relativePath.replace(startSlashRegex, '')}`;
};

/**
 * get paraent nodeid of the given nodeId,
 * nodeId is formatted as a/b/c, the parent nodeId will be a/b
 * @param nodeId
 * @returns
 */
export function getParentNodeId(nodeId: string) {
  const paths = nodeId.split(treeDelimiter);
  if (paths.length <= 1) {
    return null;
  }
  return paths.slice(0, paths.length - 1).join(treeDelimiter);
}

/**
 * if fullpath='a/b/c', return c, if fullpath='a', return a
 * @param nodeId
 * @returns
 */
export function getNodeIdByFullPath(fullpath: string) {
  const pos = fullpath.lastIndexOf(treeDelimiter);
  if (pos > -1) {
    return fullpath.substring(pos + 1);
  }
  return fullpath;
}

type TreeNodeNav = {
  component?: TreeNode['component'];
  componentProps?: TreeNode['componentProps'];
} & (NavigationItem | NavigationSectionItem | TNavigationContentItem);

/**
 * based on NavigationSectionItem build TreeNode structure recursively for Tree component
 * @param navigationItem the root navigationItem
 * @param nodePath start nodePath of the treeNode, normally will be the `heading` of the navigation
 * @param urlBasePath start urlBasePath will be attached to the urlPath for each node.
 * @returns TreeNode
 */
export function buildTreeNode(
  navigationItem: TreeNodeNav,
  nodePath: string,
  urlBasePath: string,
  customRenderTreeNode?: CustomRenderTreeNode,
): TreeNode {
  const nodeIds = new Set<string>();
  const buildTreeNodeRecursive = (nav: TreeNodeNav, nodepath: string): TreeNode => {
    let fullPath = `${nodepath}${treeDelimiter}${nav.title}`;
    // there might be nodes with same name under different `heading`, e.g. overview
    // since the headings are different, the fullPath will not be duplicated.
    // but if the title is the same, the fullPath will be duplicated.
    // so we need to add a counter to the title to make it unique.
    // examples: /docs/id-id/effects/beams and /docs/id-id/effects/highlighting
    if (nodeIds.has(fullPath)) {
      const originalPath = fullPath;
      let counter = 2;
      do {
        fullPath = `${originalPath}-${counter}`;
        counter += 1;
      } while (nodeIds.has(fullPath));
      nodeIds.add(fullPath);
    } else {
      nodeIds.add(fullPath);
    }
    const urlPath = nav.path ? generateRelativePath(urlBasePath, nav.path) : null;
    const children =
      'section' in nav ? (nav.section ?? []).map((n) => buildTreeNodeRecursive(n, fullPath)) : null;
    const node: TreeNode = {
      id: fullPath,
      value: nav.title,
      path: urlPath,
      component: nav.component,
      componentProps: nav.componentProps,
      children,
    };
    if (customRenderTreeNode) {
      node.props = {
        label: customRenderTreeNode(nav),
      };
    }
    return node;
  };
  return buildTreeNodeRecursive(navigationItem, nodePath);
}

export function traverseTree(treeNode: TreeNode, callback: (treeNode: TreeNode) => void): void {
  callback(treeNode);
  if (treeNode.children) {
    treeNode.children.forEach((node) => traverseTree(node, callback));
  }
}

export function getTreePathById(treeNode: TreeNode, id: string): TreeNode[] | null {
  const path: TreeNode[] = [];
  const getPathByIdRecursive = (node: TreeNode) => {
    path.push(node);
    if (node.id === id) {
      return true;
    }
    if (node.children) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < node.children.length; i++) {
        if (getPathByIdRecursive(node.children[i])) {
          return true;
        }
      }
    }
    path.pop();
    return false;
  };
  return getPathByIdRecursive(treeNode) ? path : null;
}

/**
 * same as buildTreeNode, but will update the nodeUrlPath to nodeId map in addition
 */
export function buildTreeNodeAndUpdatePathMap(
  navigationItem: NavigationItem | NavigationSectionItem | TNavigationContentItem,
  nodePath: string,
  urlBasePath: string,
  nodePathToIdMap: Map<string, string>,
  customRenderTreeNode?: CustomRenderTreeNode,
): TreeNode {
  const treeNode = buildTreeNode(navigationItem, nodePath, urlBasePath, customRenderTreeNode);
  traverseTree(treeNode, (n) => n.path && nodePathToIdMap.set(n.path, n.id));
  return treeNode;
}

/**
 * if try to expand a/b/c, return ['a/b/c', 'a/b', 'a']
 * @param nodeId target nodeId
 * @param expandedIds existing expanded nodeIds
 * @returns nodeIds to expand
 */
export function getNodeIdsToExpand(nodeId: string, expandedIds: string[] | null = null) {
  const set = new Set(expandedIds);
  const res: string[] = [];
  const paths = nodeId.split(treeDelimiter);
  for (let i = paths.length; i > 0; i -= 1) {
    const currNodeId = paths.slice(0, i).join(treeDelimiter);
    if (!set.has(currNodeId)) {
      res.push(currNodeId);
    }
  }
  return res;
}

/**
 * get the visible tree nodes given the root node and expanded ids
 * e.g tree = {id:1, children: [{id: 2}, {id: 3}]}
 * when expanded = [], return [1]
 * when expanded = [1], return [1,2,3]
 */
export function getVisibleTreeNodes(
  treeNode: TreeNode,
  expandedNodeIds: string[] | Set<string>,
): string[] {
  const expandedIdsSet = Array.isArray(expandedNodeIds)
    ? new Set(expandedNodeIds)
    : expandedNodeIds;
  const resultIds: string[] = [];
  const traverse = (tree: TreeNode) => {
    resultIds.push(tree.id);
    if (tree.children && expandedIdsSet.has(tree.id)) {
      tree.children.forEach((node) => traverse(node));
    }
  };
  traverse(treeNode);
  return resultIds;
}
